/**
 * Универсальный сканер кодов оборудования — одно место для терминалов и роутеров.
 *
 * Монтажник фотографирует этикетку устройства, сканер распознаёт все коды на
 * снимке и предлагает выбрать серийный номер. Коды с меткой S/N помечаются и
 * идут первыми (см. lib/barcode); если явного серийника нет, монтажник выбирает
 * из распознанных кодов сам. Снимок работает и по http:// — в отличие от живой
 * камеры (нужен HTTPS, см. lib/camera).
 */

import { useRef, useState } from 'react'

import {
  type BarcodeCandidate,
  isBarcodeScanningSupported,
  scanBarcodesFromFile,
  serialCandidates,
} from '../lib/barcode'
import { Button } from './ui'

interface Props {
  /** Заголовок — что сканируем (например, «Серийный номер терминала»). */
  title: string
  onPick: (value: string) => void
  onClose: () => void
}

type Phase = 'idle' | 'scanning' | 'results' | 'empty'

export function BarcodeScanner({ title, onPick, onClose }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [candidates, setCandidates] = useState<BarcodeCandidate[]>([])
  // Распознавание есть не везде (нужен BarcodeDetector — это Chrome на Android).
  // Кнопку «Сканировать» показываем всегда, а здесь честно объясняем, если
  // браузер не умеет: иначе кнопка молча исчезала и фича выглядела отсутствующей.
  const supported = isBarcodeScanningSupported()

  const onPhoto = async (files: FileList | null) => {
    const file = files?.[0]
    if (fileRef.current) fileRef.current.value = '' // тот же снимок можно переснять
    if (!file) return
    setPhase('scanning')
    const values = await scanBarcodesFromFile(file)
    const found = serialCandidates(values)
    if (found.length === 0) {
      setCandidates([])
      setPhase('empty')
      return
    }
    // Единственный серийник — не заставляем выбирать, отдаём сразу.
    if (found.length === 1 && found[0]!.isSerial) {
      onPick(found[0]!.value)
      return
    }
    setCandidates(found)
    setPhase('results')
  }

  return (
    <div className="scanner-backdrop" role="dialog" aria-modal="true" aria-label={title}>
      <div className="scanner-card">
        <div className="scanner-head">
          <h3>Сканирование кода</h3>
          <button className="scanner-close" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>
        <p className="subtitle">{title}</p>

        {!supported && (
          <p className="scanner-note">
            Этот браузер не умеет распознавать штрих-коды. Откройте форму в Chrome
            на телефоне — там сканер работает. Либо введите серийный номер с
            этикетки вручную: поле рядом с кнопкой доступно для ввода.
          </p>
        )}

        {supported && phase === 'idle' && (
          <p className="subtitle">
            Сфотографируйте этикетку устройства целиком — на ней может быть
            несколько кодов, серийный номер найдётся по метке S/N.
          </p>
        )}

        {phase === 'scanning' && <p className="subtitle">Распознаём коды…</p>}

        {phase === 'empty' && (
          <p className="scanner-note">
            Коды не распознаны. Снимите ближе и без бликов, чтобы штрих-код попал
            в кадр целиком, — или введите номер вручную.
          </p>
        )}

        {phase === 'results' && (
          <>
            <p className="subtitle">
              Выберите серийный номер{candidates.some((c) => c.isSerial) ? '' : ' из распознанных кодов'}:
            </p>
            <div className="scanner-list">
              {candidates.map((candidate) => (
                <button
                  key={candidate.value}
                  className="scanner-option"
                  onClick={() => onPick(candidate.value)}
                >
                  <span className="scanner-value">{candidate.value}</span>
                  {candidate.isSerial && <span className="scanner-badge">S/N</span>}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="actions">
          <Button variant="secondary" onClick={onClose}>
            {supported ? 'Отмена' : 'Ввести вручную'}
          </Button>
          {supported && (
            <label className={`btn${phase === 'scanning' ? ' busy' : ''}`}>
              {phase === 'idle' ? 'Сфотографировать' : 'Переснять'}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                disabled={phase === 'scanning'}
                onChange={(event) => void onPhoto(event.target.files)}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  )
}
